import {FULFILLED} from 'redux-promise-middleware';
import {isHtoEnabled} from 'constants/flags/flags';
import {call, select, all, takeEvery} from 'redux-saga/effects';
import {pushDataLayer} from '@optimaxdev/analytics/mobile';

import {QUOTE_ITEM} from 'constants/actions';
import {CATEGORY_STORE} from 'constants/category';
import {CURRENT_HTO_SEGMENT} from 'constants/homeTryOn/homeTryOn';
import {setHtoEnabled} from 'reducers/product/productHto/productHto';
import {getQuoteItemsCount} from 'selectors/quoteItems/quoteItems';
import {getHtoSegment, isSelectedCartHTO} from 'selectors/homeTryOn/homeTryOn';
import {getIsHtoEnabledForPdp} from 'selectors/product/hto/hto';
import {getAppliedCoupons} from 'selectors/cart/cart';
import {sendSegmentHtoEvent} from 'libs/analytics/htoAnalytics';
import {sessionStore} from 'libs/storage/storage';

/**
 * Get cart type.
 *
 * 'HTO' - when the user in HTO cart view.
 * 'Buy Now' - when the user is in Buy Now cart.
 * 'empty' - when the user has no item.
 *
 * @returns {string} - One type of cart
 */
export function* getCartType() {
    const cartItems = yield select(getQuoteItemsCount);
    if (cartItems === 0) return 'empty';
    const isHtoEnable = yield select(isSelectedCartHTO);
    return isHtoEnable ? 'HTO' : 'Buy Now';
}

/**
 * Send analytics on cart update.
 */
export function* sendHTOEvent() {
    const isExperimentEnabled = yield call(isHtoEnabled);
    if (!isExperimentEnabled) return;

    const [coupon, currentHtoSegment, isHTOCartEnabled, categoryInfo] = yield all([
        select(getAppliedCoupons),
        select(getHtoSegment),
        call(getCartType),
        call([sessionStore, 'get'], CATEGORY_STORE),
    ]);

    const htoDataLayer = {
        Coupon: coupon,
        'hto-seg': currentHtoSegment,
        'Cart - State': isHTOCartEnabled,
    };

    yield call(pushDataLayer, {
        'hto-dl':
            categoryInfo && categoryInfo.name
                ? {...htoDataLayer, Category: categoryInfo.name}
                : htoDataLayer,
    });
}

/**
 * Send analytics on HTO segment change.
 */
export function* sendSegmentHTO() {
    const isExperimentEnabled = yield call(isHtoEnabled);
    if (!isExperimentEnabled) return;

    const htoSegment = yield call([sessionStore, 'get'], CURRENT_HTO_SEGMENT);
    const currentHtoSegment = yield select(getHtoSegment);

    if (htoSegment !== currentHtoSegment) {
        yield call(sendSegmentHtoEvent, currentHtoSegment);
        yield call([sessionStore, 'set'], CURRENT_HTO_SEGMENT, currentHtoSegment);
    }
}

/**
 * Send analytics on HTO PDP state change.
 */
export function* sendPDPStateHTO() {
    const isExperimentEnabled = yield call(isHtoEnabled);
    if (!isExperimentEnabled) return;

    const isHtoEnabledForPDP = yield select(getIsHtoEnabledForPdp);
    yield call(pushDataLayer, {
        'hto-dl': {'PDP - State': isHtoEnabledForPDP ? 'On' : 'Off'},
    });
}

/**
 * HTO analytics root saga.
 */
export default function* htoCartAnalyticsSaga() {
    yield takeEvery(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`, sendHTOEvent);
    yield takeEvery(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`, sendSegmentHTO);
    yield takeEvery(setHtoEnabled, sendPDPStateHTO);
}
